import { useQuery } from '@tanstack/react-query'
import cn from 'classnames'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import { spacing } from 'ext-ui/style/spacing'
import CheckCircleBrokenIcon from 'ext-ui/svg/icon/CheckCircleBrokenIcon'
import CurrencyDollarIcon from 'ext-ui/svg/icon/CurrencyDollarIcon'
import DatabaseBackupIcon from 'ext-ui/svg/icon/DatabaseBackupIcon'
import FileXIcon from 'ext-ui/svg/icon/FileXIcon'
import FolderXIcon from 'ext-ui/svg/icon/FolderXIcon'
import UsersCheckIcon from 'ext-ui/svg/icon/UsersCheckIcon'
import React, {
  ComponentProps,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Box from 'src/components/Box/Box'
import LoadingShimmer from 'src/components/LoadingShimmer/LoadingShimmer'
import {
  TransactionsFilters,
  TransactionsFiltersBody,
} from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { ExtendCurrency } from 'src/enums'
import { accountingStatisticsQueries } from 'src/feature/AccountingSummaryBar/accountingStatistics.queries'
import StatisticsBox from 'src/feature/AccountingSummaryBar/StatisticsBox'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { getLocaleInfo } from 'src/localization/locales'
import { useTranslate } from 'src/localization/Trns'
import {
  ConnectedPlatformStatus,
  TransactionExpenseCategoryStatus,
  TransactionReceiptStatus,
} from 'src/models/transaction.models'
import { useQueryExpenseCategories } from 'src/queries/organizations/expenseCategories.queries'
import { useActiveOrgIntegration } from 'src/queries/organizations/integrations.queries'
import { useQueryPeopleManagersCount } from 'src/queries/organizations/peopleManagers.queries'
import { orgReceiptRulesQueries } from 'src/queries/organizations/receiptRules/receiptRules.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { toDollars } from 'src/utils/currency.utils'

import style from './AccountingSummaryBar.style'

export type AccountingSummaryBarProps = {
  className?: string
  filters?: Partial<TransactionsFilters>
  handleFilterChange?: (newFilters: Partial<TransactionsFilters>) => void
  resetSingleFilter?: (filterKey: keyof TransactionsFilters) => void
  apiFiltersBody?: TransactionsFiltersBody
} & Omit<ComponentProps<'div'>, 'children'>

/**
 * Accounting Summary Bar Component
 *
 * This component will fetch and display summary information based on the provided filters.
 * Depending on the user's permisisons, specific statistics will be shown.
 */
function AccountingSummaryBar(props: AccountingSummaryBarProps): ReactElement {
  const {
    className = '',
    filters,
    handleFilterChange,
    resetSingleFilter,
    apiFiltersBody,
  } = props
  const translate = useTranslate()
  const currencyFormatter = useCurrencyFormatter()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const localeCurrency = getLocaleInfo(locale).currency
  const { analytics } = useAnalyticsContext()

  const {
    data: statisticsData,
    isLoading: isLoadingStatisticsData,
    status,
  } = useQuery(accountingStatisticsQueries.statistics(apiFiltersBody))

  const [hasFetchedOnce, setHasFetchedOnce] = useState(false)

  useEffect(() => {
    if (!hasFetchedOnce && status === 'success') {
      setHasFetchedOnce(true)
    }
  }, [status, hasFetchedOnce])

  const spendStatistics = useMemo(() => {
    // default to showing $0 in the user's locale currency
    if ((statisticsData?.currencies || []).length === 0) {
      return [
        {
          cents: 0,
          currency: localeCurrency,
        },
      ]
    }
    // else show spend for each of the user's supported currencies

    const spendCurrenciesSorted = (statisticsData?.currencies || []).sort(
      (a, b) => {
        // sort so that locale currency is first
        // if there is no locale currency in the list, order doesn't matter
        if (b === localeCurrency) return 1
        return -1
      },
    )

    const spend = spendCurrenciesSorted.map((currency) => {
      switch (currency) {
        case ExtendCurrency.Usd:
          return {
            cents: statisticsData?.usdTotalCardSpendCents || 0,
            currency: ExtendCurrency.Usd,
          }
        case ExtendCurrency.Cad:
          return {
            cents: statisticsData?.cadTotalCardSpendCents || 0,
            currency: ExtendCurrency.Cad,
          }
        default:
          return {
            cents: 0,
            currency,
          }
      }
    })

    return spend
  }, [statisticsData])

  // Business logic to determine what stats to show

  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const { data: orgManagersCount } = useQueryPeopleManagersCount({
    organizationId: organization?.id,
  })
  const orgHasManagers = (orgManagersCount || 0) > 0

  const { data: receiptRulesData } = useQuery(
    orgReceiptRulesQueries.detail(organization?.id),
  )
  const receiptRulesEnabled = receiptRulesData?.receiptRule?.enabled

  const { data: requiredExpenseCategories } = useQueryExpenseCategories({
    filters: { active: true, requiredForTransactions: true },
  })
  const orgHasRequiredCategories =
    requiredExpenseCategories && requiredExpenseCategories.length > 0

  const { data: orgErpIntegration } = useActiveOrgIntegration({
    orgId: organization?.id,
  })

  // -----------------------------------------------

  const statistics = useMemo(() => {
    const statisticsBoxes = new Array()

    if (orgHasManagers) {
      const isNeedsManagerApprovalActive = !!filters?.needsManagerApproval
      statisticsBoxes.push({
        icon: <UsersCheckIcon />,
        label: translate('Pending Manager Review'),
        value: statisticsData?.pendingManagerReview,
        tooltipCopy: translate(
          'Shows count of transactions that are missing manager review based on your filters.',
        ),
        active: isNeedsManagerApprovalActive,
        onClick: () => {
          analytics?.track('accounting summary quick filters', {
            quickfilter: 'needsManagerApproval',
          })

          if (isNeedsManagerApprovalActive) {
            resetSingleFilter?.('needsManagerApproval')
            return
          }

          handleFilterChange?.({
            ...filters,
            needsManagerApproval: true,
          })
        },
      })
    }

    const isNeedsFinalApprovalActive = !!filters?.needsFinalApproval
    statisticsBoxes.push({
      icon: <CheckCircleBrokenIcon />,
      label: translate('Pending Final Approval'),
      value: statisticsData?.pendingFinalApproval,
      tooltipCopy: translate(
        'Shows count of transactions that are pending final approval based on your filters.',
      ),
      active: isNeedsFinalApprovalActive,
      onClick: () => {
        analytics?.track('accounting summary quick filters', {
          quickfilter: 'needsFinalApproval',
        })

        if (isNeedsFinalApprovalActive) {
          resetSingleFilter?.('needsFinalApproval')
          return
        }

        handleFilterChange?.({
          ...filters,
          needsFinalApproval: true,
        })
      },
    })

    if (receiptRulesEnabled) {
      const isMissingReceiptsActive =
        filters?.receiptStatus?.size === 1 &&
        filters?.receiptStatus?.has(TransactionReceiptStatus.Missing) &&
        filters?.receiptRequired === true
      statisticsBoxes.push({
        icon: <FileXIcon />,
        label: translate('Missing Receipts'),
        value: statisticsData?.missingReceipts,
        tooltipCopy: translate(
          'Shows count of transactions that are missing receipts based on your filters.',
        ),
        // filtering by ONLY missing receipts
        active: isMissingReceiptsActive,
        onClick: () => {
          analytics?.track('accounting summary quick filters', {
            quickfilter: 'missingReceipts',
          })

          if (isMissingReceiptsActive) {
            resetSingleFilter?.('receiptStatus')
            resetSingleFilter?.('receiptRequired')
            return
          }

          handleFilterChange?.({
            ...filters,
            receiptStatus: new Set<string>([TransactionReceiptStatus.Missing]),
            receiptRequired: true,
          })
        },
      })
    }

    if (orgHasRequiredCategories) {
      const isMissingCategoriesActive =
        filters?.expenseCategoryStatuses?.size === 1 &&
        filters?.expenseCategoryStatuses?.has(
          TransactionExpenseCategoryStatus.Missing,
        )
      statisticsBoxes.push({
        icon: <FolderXIcon />,
        label: translate('Missing Categories'),
        value: statisticsData?.missingCategories,
        tooltipCopy: translate(
          'Shows count of transactions that are missing expense categories based on your filters.',
        ),
        // filtering by ONLY missing categories
        active: isMissingCategoriesActive,
        onClick: () => {
          analytics?.track('accounting summary quick filters', {
            quickfilter: 'missingCategories',
          })

          if (isMissingCategoriesActive) {
            resetSingleFilter?.('expenseCategoryStatuses')
            return
          }

          handleFilterChange?.({
            ...filters,
            expenseCategoryStatuses: new Set<string>([
              TransactionExpenseCategoryStatus.Missing,
            ]),
          })
        },
      })
    }

    if (orgErpIntegration) {
      const isNotSyncedActive =
        filters?.syncStatuses?.size === 3 &&
        !filters?.syncStatuses?.has(ConnectedPlatformStatus.Success)
      statisticsBoxes.push({
        icon: <DatabaseBackupIcon />,
        label: translate('Not Synced to ${erp}', {
          erp: orgErpIntegration.platformName,
        }),
        value: statisticsData?.notSyncedToErp,
        tooltipCopy: translate(
          'Shows count of transactions that have not been synced to your accounting platform based on your filters.',
        ),
        // filtering by anything that is NOT synced
        active: isNotSyncedActive,
        onClick: () => {
          analytics?.track('accounting summary quick filters', {
            quickfilter: 'notSyncedToERP',
          })

          if (isNotSyncedActive) {
            resetSingleFilter?.('syncStatuses')
            return
          }

          handleFilterChange?.({
            ...filters,
            syncStatuses: new Set<string>([
              ConnectedPlatformStatus.NotSynced,
              ConnectedPlatformStatus.Failed,
              ConnectedPlatformStatus.Pending,
            ]),
          })
        },
      })
    }

    return statisticsBoxes
  }, [
    filters,
    statisticsData,
    orgHasManagers,
    receiptRulesEnabled,
    orgHasRequiredCategories,
    orgErpIntegration,
  ])

  // Scroll fade handling
  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    const scrollContainer = event.currentTarget.parentElement
    const statisticsList = event.currentTarget

    if (!scrollContainer || !statisticsList) return

    const { scrollLeft, scrollWidth, clientWidth } = statisticsList

    // Check if scrolled from left (show left fade if not at start)
    const isScrolledFromLeft = scrollLeft > 0

    // Check if scrolled from right (show right fade if not at end)
    const isScrolledFromRight = scrollLeft < scrollWidth - clientWidth

    // Toggle classes on the scroll container
    if (isScrolledFromLeft) {
      scrollContainer.classList.add('show-left-fade')
    } else {
      scrollContainer.classList.remove('show-left-fade')
    }

    if (isScrolledFromRight) {
      scrollContainer.classList.add('show-right-fade')
    } else {
      scrollContainer.classList.remove('show-right-fade')
    }
  }

  // Initialize fades on component mount and when statistics change
  useEffect(() => {
    const updateFades = () => {
      const scrollContainer = document.querySelector(
        '.AccountingSummaryBar-scrollContainer',
      )
      const statisticsList = scrollContainer?.querySelector(
        '.AccountingSummaryBar-scrollContainer-statisticsList',
      )

      if (!scrollContainer || !statisticsList) return

      const { scrollWidth, clientWidth } = statisticsList as HTMLElement

      // Initially, show right fade if content overflows
      if (scrollWidth > clientWidth) {
        scrollContainer.classList.add('show-right-fade')
      } else {
        scrollContainer.classList.remove('show-right-fade')
      }

      // Left fade should be hidden initially (since we start at scrollLeft = 0)
      scrollContainer.classList.remove('show-left-fade')
    }

    // Initial calculation
    updateFades()

    // Add resize listener
    window.addEventListener('resize', updateFades)

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateFades)
    }
  }, [statistics, isLoadingStatisticsData])

  return (
    <Box
      className={cn('AccountingSummaryBar', className)}
      outlineBorder
      padding={spacing(8)}
      css={style}
    >
      <div className={'AccountingSummaryBar-spendCents'}>
        <div className="AccountingSummaryBar-spendCents-header">
          <div className="AccountingSummaryBar-spendCents-header-left">
            {<CurrencyDollarIcon />}
            <div className="AccountingSummaryBar-spendCents-header-left-label">
              {translate('Total Card Spend')}
            </div>
          </div>
          <TooltipIcon className="AccountingSummaryBar-spendCents-tooltip">
            {translate('Total card spend based on your filters.')}
          </TooltipIcon>
        </div>
        <div className="AccountingSummaryBar-spendCents-statistics">
          {isLoadingStatisticsData && (
            <LoadingShimmer width={'200px'} height={'36px'} />
          )}
          {!isLoadingStatisticsData &&
            spendStatistics.map((spend) => (
              <div
                className="AccountingSummaryBar-spendCents-statistics-value"
                key={spend.currency}
              >
                {currencyFormatter({
                  amount: toDollars(spend.cents),
                  forceHideCurrency: true,
                })}
                {(spendStatistics.length > 1 ||
                  spend.currency !== localeCurrency) && (
                  <div className="AccountingSummaryBar-spendCents-statistics-value-currency">
                    {spend.currency}
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
      <div className="AccountingSummaryBar-scrollContainer">
        <div
          className="AccountingSummaryBar-scrollContainer-statisticsList"
          onScroll={handleScroll}
        >
          {!hasFetchedOnce && (
            <>
              <LoadingShimmer width={'232px'} height={'102px'} />
              <LoadingShimmer width={'232px'} height={'102px'} />
              <LoadingShimmer width={'232px'} height={'102px'} />
            </>
          )}
          {hasFetchedOnce &&
            statistics.map((stat) => (
              <StatisticsBox
                key={stat.label}
                isLoading={isLoadingStatisticsData}
                {...stat}
              />
            ))}
        </div>
      </div>
    </Box>
  )
}

export default AccountingSummaryBar
